import { Injectable } from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
} from '@angular/forms';

@Injectable({
  providedIn: 'root',
})

export class FormValidatorService {
  constructor() {}

  /**
   *check if the control is invalid or has been touched
   *
   * @param {FormGroup} formGroup
   * @param {string} controlName
   * @return {*}
   * @memberof FormValidatorService
   */
  ControlIsInvalid(formGroup: FormGroup, controlName: string) {
      if(formGroup.controls[controlName] != undefined){
          return formGroup.controls[controlName]?.invalid &&
          (formGroup.controls[controlName]?.dirty ||
          formGroup.controls[controlName]?.touched);
      }
      return false;
    }

  isFieldInvalid(formGroup:FormGroup, controlName: string): boolean {
    const control = formGroup.get(controlName);
    return control ? (control.touched && control.invalid) : false;
  }

  IsRequired(formGroup: FormGroup, controlName: string) {
    const control = formGroup.get(controlName);
    if(formGroup.controls[controlName] != undefined){
      return formGroup.controls[controlName].hasError('required')
      && this.ControlIsInvalid(formGroup, controlName);
    }
    return false;
  }
  /**
   * check if the control is null or empty
   *
   * @param {FormGroup} formGroup
   * @param {string} controlName
   * @return {*}
   * @memberof FormValidatorService
   */
  IsNullorEmpty(formGroup: FormGroup, controlName: string) {
    if(formGroup.controls[controlName] != undefined){
      return (
        formGroup.controls[controlName].hasError('required') &&
        this.ControlIsInvalid(formGroup, controlName)
      );
    }
    return false;
  }

    /**
   * check if the control email is valid
   *
   * @param {FormGroup} formGroup
   * @param {string} controlName
   * @return {*}
   * @memberof FormValidatorService
   */
    IsValidEmail(formGroup: FormGroup, controlName: string) {
      if(formGroup.controls[controlName] != undefined){
        return (
          formGroup.controls[controlName].hasError('email') &&
          this.ControlIsInvalid(formGroup, controlName)
        );
      }
      return false;
    }

  /**
   *check if the control has a min length error
   *
   * @param {FormGroup} formGroup
   * @param {string} controlName
   * @return {*}
   * @memberof FormValidatorService
   */
  MinLengthError(formGroup: FormGroup, controlName: string) {
    if(formGroup.controls[controlName] != undefined){
      return (
        formGroup.controls[controlName].hasError('minlength') &&
        this.ControlIsInvalid(formGroup, controlName)
      );
    }
    return false;
  }
  /**
   *check if the control has a max length error
   *
   * @param {FormGroup} formGroup
   * @param {string} controlName
   * @return {*}
   * @memberof FormValidatorService
   */
  MaxLengthError(formGroup: FormGroup, controlName: string) {
    if(formGroup.controls[controlName] != undefined){
      return (
        formGroup.controls[controlName].hasError('maxlength') &&
        this.ControlIsInvalid(formGroup, controlName)
      );
    }
     return false;
  }

  /**
   *check if the control has a pattern error
   *
   * @param {FormGroup} formGroup
   * @param {string} controlName
   * @return {*}
   * @memberof FormValidatorService
   */
  PatternError(formGroup: FormGroup, controlName: string) {
    if(formGroup.controls[controlName] != undefined){
      return (
        formGroup.controls[controlName].hasError('pattern') &&
        this.ControlIsInvalid(formGroup, controlName)
      );
    }
    return false;
  }

  /**
   *check if the control has a digits only error
   *
   * @param {FormGroup} formGroup
   * @param {string} controlName
   * @return {*}
   * @memberof FormValidatorService
   */
  DigitsOnlyError(formGroup: FormGroup, controlName: string) {
    if(formGroup.controls[controlName] != undefined){
      return (
        formGroup.controls[controlName].hasError('pattern') &&
        formGroup.controls[controlName].getError('pattern')?.requiredPattern ===
          '\\d+' &&
        this.ControlIsInvalid(formGroup, controlName)
      );
    }
    return false;
  }
  /**
  *check if the control has a min value error
  *
  * @param {FormGroup} formGroup
  * @param {string} controlName
  * @param {number} minValue
  * @return {*}
  * @memberof FormValidatorService
  */
  MinValueError(formGroup: FormGroup, controlName: string, minValue?: number, control?: AbstractControl | null) {
    if(!control){
      control = formGroup.controls[controlName];
    }

    if (control) {
      if (minValue === undefined) {
        const minValidator = control.validator ? control.validator({} as AbstractControl) : null;
      }
      return (
        control.value !== null &&
        control.value !== undefined &&
        minValue !== undefined &&
        control.value < minValue &&
        this.ControlIsInvalid(formGroup, controlName)
      );
    }
    return false;
  }


  MinValueValidate(control?: AbstractControl | null, minValue?: number) {
    if(!control) return false;

    if (control) {
      if (minValue === undefined) {
        if (!control.hasError('minValue')) {
          control.setErrors({ ...control.errors, minValue: true });
        }
        return false;
      }
      return (
        (control.value === null ||
        control.value === undefined ||
        minValue !== undefined) &&
        control.value < minValue
      );
    }
    return false;
  }

  MaxValueValidate(control?: AbstractControl | null, minValue?: number) {
    if(!control) return false;

    if (control) {
      if (minValue === undefined) {
        if (!control.hasError('maxValue')) {
          control.setErrors({ ...control.errors, maxValue: true });
        }
        return false;
      }
      return (
        (control.value === null ||
        control.value === undefined ||
        minValue !== undefined) &&
        control.value > minValue
      );
    }
    return false;
  }

  /**
   *check if the control has a min value error
   *
   * @param {FormGroup} formGroup
   * @param {string} controlName
   * @param {string} minValue
   * @return {*}
   * @memberof FormValidatorService
   */
  MinStringValueError(formGroup: FormGroup, controlName: string, minValue: string
  ) {
    if(formGroup.controls[controlName] != undefined){
      return (
        formGroup.controls[controlName].value == '' &&
        this.ControlIsInvalid(formGroup, controlName)
      );
    }
    return false;
  }

  /**
   *get the list form group by index
   *
   * @param {FormGroup} formGroup
   * @param {number} i
   * @param {string} listName
   * @return {*}  {FormGroup}
   * @memberof FormValidatorService
   */
  getListFormGroup(formGroup: FormGroup, i: number, listName: string): FormGroup {
    if(formGroup != undefined){
      return (formGroup.get(listName) as FormArray)?.controls[i] as FormGroup<any>;
    }
    return formGroup;
  }

  militaryTimeValidator(control: AbstractControl | null):  boolean {
    if (!control) return false;
    const timeRegex = /^([01]\d|2[0-3]):([0-5]\d)$/;

    const isValid = timeRegex.test(control.value);

    if (!isValid) {
      if (!control.hasError('invalidTime')) {
        control.setErrors({ ...control.errors, invalidTime: true });
      }
      return false;
    }
      const errors = control.errors;
      if (errors && errors['invalidTime']) {
        delete errors['invalidTime'];

      if (Object.keys(errors).length === 0) {
        control.setErrors(null);
      } else {
        control.setErrors(errors);
      }

      return true;
    }
    return isValid;
  }

  IsMandatory(control: AbstractControl | null):  boolean {
    if (!control) return true;

    if(control === undefined || control === null || control.value === '' ){
      if (!control.hasError('required')) {
        control.setErrors({ ...control.errors, required: true });
      }
      return true;
    } else {
      const errors = control.errors;
      if (errors && errors['required']) {
        delete errors['required'];
        if (Object.keys(errors).length === 0) {
          control.setErrors(null);
        } else {
          control.setErrors(errors);
        }
        return false;
      }
      return false;
    }
  }
}

/**
 * Validates the date range between two form controls.
 * Returns a validation error if the start date is greater than the end date.
 *
 * @param startDateKey The key of the start date form control.
 * @param endDateKey The key of the end date form control.
 * @returns A ValidatorFn function that validates the date range.
 */
export function dateRangeValidator(
  startDateKey: string,
  endDateKey: string
): ValidatorFn {
  return (formGroup: AbstractControl): { [key: string]: any } | null => {
    const startDate = formGroup.get(startDateKey)?.value;
    const endDate = formGroup.get(endDateKey)?.value;

    if (startDate && endDate && startDate > endDate) {
      return { dateRangeInvalid: true };
    }
    return null;
  };
}


/**
 * Validates the price interval by checking if the minimum value is greater than the maximum value.
 * If the minimum value is greater than the maximum value, it sets an error and returns an error object.
 * Otherwise, it clears the error if the condition is met and returns null.
 *
 * @returns A validator function that can be used with Angular Reactive Forms.
 */
export function intervalValidator(): ValidatorFn {
  return (control: AbstractControl): { [key: string]: any } | null => {

    const min = control.get('priceIntervalMin')?.value;
    const max = control.get('priceIntervalMax')?.value;
    if(max>0){
      if (min != null && max != null && min > max) {
        control.get('priceIntervalMax')?.setErrors({ ...(control.get('priceIntervalMax')?.errors || {}), maxLessThanMin: true });
        return { maxLessThanMin: true };
      } else {
        // Clear the error if the condition is met
        if(min != null && max != null){
          if (control.get('priceIntervalMax')?.errors) {
            control.get('priceIntervalMax')?.setErrors({ ...(control.get('priceIntervalMax')?.errors || {}), maxLessThanMin: false });
            control.get('priceIntervalMax')?.updateValueAndValidity();
          }
        }

      }
    }
    return null; // If interval is valid, return null
  };


}

// Custom Validator for the Form Array
export function validateIntervals(formArray: AbstractControl): { [key: string]: any } | null {

  // const intervals = formArray as FormArray;
  //  for (let i = 1; i < intervals.length; i++) {
  //   const prevInterval = intervals.at(i - 1) as FormGroup;
  //   const currentInterval = intervals.at(i) as FormGroup;
  //   const prevMax = prevInterval.get('priceIntervalMax')?.value;
  //   const currentMin = currentInterval.get('priceIntervalMin')?.value;

  //   if (currentMin <= prevMax) {
  //     currentInterval.get('priceIntervalMin')?.setErrors({ ...(currentInterval.get('priceIntervalMin')?.errors || {}), minLessThanOrEqualLastMax: true });
  //     return { minLessThanOrEqualLastMax: true };
  //   } else {
  //     if (currentInterval.get('priceIntervalMin')?.hasError('minLessThanOrEqualLastMax')) {

  //       currentInterval.get('priceIntervalMin')?.setErrors({ ...(currentInterval.get('priceIntervalMin')?.errors || {}), minLessThanOrEqualLastMax: false });
  //       currentInterval.get('priceIntervalMin')?.updateValueAndValidity();

  //     }
  //   }
  // }
  return null;
}


export function uuidValidator(): ValidatorFn {
  return (control: AbstractControl): { [key: string]: any } | null => {
    const isValidUuid = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/.test(control.value);
    return isValidUuid ? null : { 'invalidUuid': { value: control.value } };
  };
}


export function militaryTimeValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const timeRegex = /^([01]\d|2[0-3]):([0-5]\d)$/;  // Regex for HH:MM format
    const valid = timeRegex.test(control.value);
    return valid ? null : { militaryTime: true };
  };
}

export function validateMilitaryTime(control: AbstractControl, swError: boolean): boolean {
  const timeRegex = /^([01]\d|2[0-3]):([0-5]\d)$/;  // Regex para formato HH:MM

  // Validar el valor del control usando la expresión regular
  const isValid = timeRegex.test(control.value);

  if (!isValid) {
    // Si no cumple con la expresión regular, se actualiza swError a true
    swError = true;

    // Agregar un error personalizado al control
    control.setErrors({ invalidTime: true });
  } else {
    // Si cumple con la expresión regular, se limpia el error 'invalidTime' si existe
    const errors = control.errors;
    if (errors && errors['invalidTime']) {
      delete errors['invalidTime'];

      // Si no quedan otros errores, limpiar el objeto errors
      if (Object.keys(errors).length === 0) {
        control.setErrors(null);
      } else {
        control.setErrors(errors);
      }
    }
  }

  return swError;
}



